import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, useReducedMotion } from "framer-motion";
import { BrandVariant } from "@/lib/brandConfig";
import bbqHero from "@/assets/bbq-hero-outdoor-kitchen.jpg";

// Brand-specific lifestyle images (BBQ only)
const BRAND_IMAGES: Record<BrandVariant, string> = {
  bbq: bbqHero,
};

// Brand color configurations (BBQ only)
const BRAND_COLORS: Record<BrandVariant, {
  accent: string;
  accentHover: string;
  accentLight: string;
  overlayGradient: string;
}> = {
  bbq: {
    accent: "hsl(24, 95%, 53%)",
    accentHover: "hsl(24, 95%, 45%)",
    accentLight: "hsl(24, 95%, 95%)",
    overlayGradient: "from-[hsl(24,95%,15%)/70] to-transparent",
  },
};

interface PremiumModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  brand: BrandVariant;
  children: React.ReactNode;
  imageUrl?: string;
  imageAlt?: string;
  showImage?: boolean;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl";
}

const maxWidthClasses = {
  sm: "sm:max-w-md",
  md: "sm:max-w-lg",
  lg: "sm:max-w-xl",
  xl: "sm:max-w-2xl",
  "2xl": "sm:max-w-3xl",
};

export const PremiumModalShell = ({
  isOpen,
  onClose,
  brand,
  children,
  imageUrl,
  imageAlt = "Lifestyle image",
  showImage = true,
  maxWidth = "2xl",
}: PremiumModalShellProps) => {
  const shouldReduceMotion = useReducedMotion();
  const colors = BRAND_COLORS[brand];
  const defaultImage = BRAND_IMAGES[brand];

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        {/* Overlay - softer backdrop */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        
        {/* Content */}
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 w-[95vw] translate-x-[-50%] translate-y-[-50%]",
            "bg-white shadow-2xl overflow-hidden",
            "rounded-xl sm:rounded-2xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "duration-300",
            maxWidthClasses[maxWidth]
          )}
          aria-describedby={undefined}
        >
          {/* Hidden title for accessibility */}
          <VisuallyHidden>
            <DialogPrimitive.Title>Request a Quote</DialogPrimitive.Title>
          </VisuallyHidden>
          <motion.div
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="flex flex-col md:flex-row max-h-[90vh] md:max-h-[85vh]"
          >
            {/* Left side - Image (hidden on mobile, shown on desktop) */}
            {showImage && (
              <div className="hidden md:block relative w-[45%] min-h-[500px] flex-shrink-0">
                <img
                  src={imageUrl || defaultImage}
                  alt={imageAlt}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Subtle overlay for text readability if needed */}
                <div className={cn(
                  "absolute inset-0 bg-gradient-to-t",
                  colors.overlayGradient
                )} />
              </div>
            )}

            {/* Right side - Form content */}
            <div className={cn(
              "flex-1 flex flex-col overflow-y-auto",
              showImage ? "md:w-[55%]" : "w-full"
            )}>
              {/* Close button */}
              <DialogPrimitive.Close
                className="absolute right-4 top-4 z-10 p-2 rounded-full bg-white/90 text-neutral-400 hover:text-neutral-600 hover:bg-white transition-all shadow-sm"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </DialogPrimitive.Close>

              {/* Content area with proper padding */}
              <div className="flex-1 p-6 sm:p-8 lg:p-10">
                {children}
              </div>
            </div>
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

// Shared input styles for consistency
export const premiumInputStyles = cn(
  "h-12 w-full px-4",
  "bg-neutral-50 border border-neutral-200",
  "rounded-lg",
  "text-neutral-800 placeholder:text-neutral-400",
  "focus:outline-none focus:ring-2 focus:ring-offset-0",
  "focus:border-transparent transition-all duration-200"
);

// Get focus ring color by brand (BBQ only)
export const getBrandFocusRing = (brand: BrandVariant) => {
  return "focus:ring-orange-500/30";
};

// Shared button styles (BBQ only)
export const getPrimaryButtonStyles = (brand: BrandVariant) => {
  return cn(
    "w-full h-12 text-base font-medium text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg",
    "bg-orange-500 hover:bg-orange-600"
  );
};

export const getSecondaryButtonStyles = (brand: BrandVariant) => {
  return cn(
    "w-full h-12 text-base font-medium rounded-lg border transition-all duration-200",
    "border-neutral-200 text-neutral-600 hover:bg-neutral-50"
  );
};

export { BRAND_IMAGES, BRAND_COLORS };
